import { create } from 'zustand';
import { logger } from '../utils/logger';

/**
 * WARNING: DO NOT IMPORT THIS STORE DIRECTLY IN COMPONENTS.
 * Always use the useInitialLoadingScreenManager hook from useInitialLoadingScreenManager.ts instead.
 * Direct usage of this store can cause circular dependencies and state management issues.
 *
 * Simplified loading screen state interface focused only on what's needed:
 * 1. State values
 * 2. UI actions
 */
interface InitialLoadingScreenUIState {
  // STATE VALUES
  /** Whether the loading screen is visible */
  isVisible: boolean;

  // UI ACTIONS
  /** Hide the loading screen */
  hideLoadingScreen: () => void;
  /** Show the loading screen */
  showLoadingScreen: () => void;
}

export const useInitialLoadingScreenUIStore = create<InitialLoadingScreenUIState>((set, get) => ({
  isVisible: false, // Start with loading screen hidden

  hideLoadingScreen: () => {
    // Only proceed if the loading screen is still visible
    if (!get().isVisible) return;

    logger.component('Hiding global loading screen');

    // Get the loading screen element
    const loader = document.getElementById('app-loading');
    if (loader) {
      // Add the hide class to trigger the fade-out transition
      loader.classList.add('hide');

      // After the transition completes, remove the element from the DOM
      setTimeout(() => {
        if (loader.parentNode) {
          loader.parentNode.removeChild(loader);
        }
      }, 300); // Matches the CSS transition duration
    }

    // Update the state
    set({ isVisible: false });
  },

  showLoadingScreen: () => {
    // Only proceed if the loading screen isn't already visible
    if (get().isVisible) return;

    // Don't show loading screen on public routes (landing page and auth routes)
    const currentPath = window.location.pathname;
    if (currentPath === '/' || currentPath === '' || currentPath.startsWith('/auth/')) {
      logger.component('Skipping loading screen for public route');
      return;
    }

    logger.component('Showing global loading screen');

    // Get the loading screen element
    const loader = document.getElementById('app-loading');
    if (loader) {
      // Remove the hide class to make it visible
      loader.classList.remove('hide');

      // Update the state
      set({ isVisible: true });
    }
  },
}));
